import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, LogOut, CreditCard, FolderOpen, BookOpen, FileText, HelpCircle, Users, GraduationCap, Home, AlertTriangle, Award, ClipboardList, GripVertical, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTutorial } from "@/hooks/useTutorial";
import { useCourseContext } from "@/contexts/CourseContext";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface Workspace {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  created_at: string;
  course_count?: number;
  material_count?: number;
}

interface DashboardStats {
  workspaces: number;
  courses: number;
  learners: number;
}

interface AtRiskLearner {
  userId: string;
  courseId: string;
  courseTitle: string;
  email: string;
  attempts: { score: number; passed: boolean; taken_at: string; nodeTitle: string }[];
  passRate: number;
  lastThreeFailed: boolean;
  avgScore: number;
  actionPlan?: string | null;
}

interface StarLearner {
  userId: string;
  courseId: string;
  courseTitle: string;
  email: string;
  attempts: { score: number; passed: boolean; taken_at: string; nodeTitle: string }[];
  passRate: number;
  avgScore: number;
}

interface RecentActivity {
  learnerEmail: string;
  courseTitle: string;
  nodeTitle: string;
  passed: boolean;
  score: number;
  takenAt: string;
}

const DASHBOARD_CACHE_STALE_MS = 2 * 60 * 1000; // 2 min - avoid refetch on tab switch / route return

const Dashboard = () => {
  const { session } = useAuth();
  const { userType, isLoading: typeLoading } = useUserType(session?.user?.id);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const queryClient = useQueryClient();
  const [newWorkspace, setNewWorkspace] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [dragCourseId, setDragCourseId] = useState<string | null>(null);
  const [dragOverWorkspaceId, setDragOverWorkspaceId] = useState<string | null>(null);
  const lastDropAtRef = useRef(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCourseContext } = useCourseContext();
  const { restartTutorial } = useTutorial();

  // "Others" (one free course flow) go to /create, not dashboard
  useEffect(() => {
    if (!session || typeLoading) return;
    if (userType === "other_no_course" || userType === "other_one_course") {
      navigate("/create", { replace: true });
    }
  }, [session, userType, typeLoading, navigate]);

  // Set dashboard context
  useEffect(() => {
    setCourseContext({
      courseId: null,
      courseName: null,
      nodeId: null,
      nodeName: null,
      pageType: "dashboard",
    });
  }, []);

  useEffect(() => {
    if (!session) {
      navigate("/auth");
    } else {
      checkOnboarding(session.user.id);
    }
  }, [session, navigate]);

  const checkOnboarding = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("id", userId)
        .single();

      if (error && error.code === '42703') {
        console.warn('onboarding_completed column not found, skipping check');
        return;
      }

      if (error) {
        console.error('Error checking onboarding:', error);
        return;
      }

      if (profile && !profile.onboarding_completed) {
        navigate("/onboarding");
      }
    } catch (err) {
      console.error('Error in checkOnboarding:', err);
    }
  };

  // Cached workspaces + stats - shows instantly when returning from another route/tab
  const {
    data: workspacesData,
    isLoading: loading,
    isError: workspacesError,
    error: workspacesErrorObj,
  } = useQuery({
    queryKey: ["dashboard-workspaces", session?.user?.id],
    queryFn: async () => {
      const { data: workspaceData, error: workspaceError } = await supabase
        .from("workspaces")
        .select("*")
        .order("created_at", { ascending: false });

      if (workspaceError) {
        // 42P01 = table not found, 42501 = insufficient privilege
        if (workspaceError.code === "42P01" || workspaceError.code === "42501" || workspaceError.message?.includes("permission denied")) {
          return { workspaces: [], stats: { workspaces: 0, courses: 0, learners: 0 } };
        }
        throw workspaceError;
      }

      const workspacesWithCounts = await Promise.all(
        (workspaceData || []).map(async (ws) => {
          const [coursesResult, materialsResult] = await Promise.all([
            supabase
              .from("learning_projects")
              .select("id", { count: "exact", head: true })
              .eq("workspace_id", ws.id),
            supabase
              .from("materials")
              .select("id", { count: "exact", head: true })
              .eq("workspace_id", ws.id)
              .is("project_id", null),
          ]);
          return {
            ...ws,
            course_count: coursesResult.count || 0,
            material_count: materialsResult.count || 0,
          };
        })
      );

      const totalCourses = (workspacesWithCounts || []).reduce((sum, ws) => sum + (ws.course_count || 0), 0);
      const workspaceIds = (workspacesWithCounts || []).map((ws) => ws.id);
      let totalLearners = 0;
      if (workspaceIds.length > 0) {
        const { data: courses } = await supabase
          .from("learning_projects")
          .select("id")
          .in("workspace_id", workspaceIds);
        const courseIds = (courses || []).map((c) => c.id);
        if (courseIds.length > 0) {
          const { data: enrollments } = await supabase
            .from("user_course_enrollments")
            .select("user_id")
            .in("course_id", courseIds);
          totalLearners = new Set((enrollments || []).map((e) => e.user_id)).size;
        }
      }

      return {
        workspaces: workspacesWithCounts,
        stats: {
          workspaces: workspacesWithCounts?.length || 0,
          courses: totalCourses,
          learners: totalLearners,
        },
      };
    },
    enabled: !!session?.user?.id,
    staleTime: DASHBOARD_CACHE_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const workspaces = workspacesData?.workspaces ?? [];
  const stats = workspacesData?.stats ?? { workspaces: 0, courses: 0, learners: 0 };

  // Courses not assigned to any workspace
  const { data: unassignedCourses = [] } = useQuery({
    queryKey: ["dashboard-unassigned-courses", session?.user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("learning_projects")
        .select("id, title, description, created_at")
        .eq("user_id", session!.user.id)
        .is("workspace_id", null)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!session?.user?.id,
    staleTime: DASHBOARD_CACHE_STALE_MS,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (workspacesError && workspacesErrorObj) {
      toast({
        variant: "destructive",
        title: "Error loading workspaces",
        description: (workspacesErrorObj as Error).message,
      });
    }
  }, [workspacesError, workspacesErrorObj, toast]);

  // Cached learner insights (non-blocking: failures e.g. CORS or function not deployed won't break the dashboard)
  const { data: learnerInsights, isLoading: insightsLoading } = useQuery({
    queryKey: ["dashboard-learner-insights", session?.user?.id],
    queryFn: async () => {
      try {
        const { data, error } = await invokeFunction("get-dashboard-learner-insights", { body: {} });
        if (error) {
          console.warn("Learner insights unavailable:", error);
          return { atRiskLearners: [], starLearners: [], recentActivity: [] };
        }
        return data;
      } catch (e) {
        console.warn("Learner insights request failed (check CORS / function deployment):", e);
        return { atRiskLearners: [], starLearners: [], recentActivity: [] };
      }
    },
    enabled: !!session?.user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const handleCreateWorkspace = async () => {
    if (!newWorkspace.name.trim()) {
      toast({
        variant: "destructive",
        title: "Name required",
        description: "Please enter a workspace name",
      });
      return;
    }

    setCreating(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in",
      });
      setCreating(false);
      return;
    }

    let data: any = null;
    let error: any = null;

    // Try direct insert first
    const direct = await supabase
      .from("workspaces")
      .insert({
        user_id: user.id,
        name: newWorkspace.name,
        description: newWorkspace.description || null,
      })
      .select()
      .single();

    if (direct.error) {
      // Fallback to RPC (bypasses RLS)
      const rpc = await supabase.rpc("create_workspace", {
        p_name: newWorkspace.name,
        p_description: newWorkspace.description || "",
      });
      data = rpc.data;
      error = rpc.error;
    } else {
      data = direct.data;
    }

    if (error) {
      toast({
        variant: "destructive",
        title: "Error creating workspace",
        description: error.message,
      });
    } else if (data) {
      queryClient.invalidateQueries({ queryKey: ["dashboard-workspaces"] });
      toast({ title: "Workspace created!" });
      setNewWorkspace({ name: "", description: "" });
      setShowCreateWorkspace(false);
      navigate(`/workspace/${data.id}`);
    }
    setCreating(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const handleAssignCourseToWorkspace = async (courseId: string, workspaceId: string) => {
    if (!session?.user?.id) return;
    setDragCourseId(null);
    setDragOverWorkspaceId(null);
    const { error } = await supabase
      .from("learning_projects")
      .update({ workspace_id: workspaceId, updated_at: new Date().toISOString() })
      .eq("id", courseId)
      .eq("user_id", session.user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not move course",
        description: error.message,
      });
      return;
    }
    toast({ title: "Course moved to workspace" });
    queryClient.invalidateQueries({ queryKey: ["dashboard-workspaces"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-unassigned-courses"] });
  };

  const handleDeleteUnassignedCourse = async (courseId: string) => {
    if (!session?.user?.id) return;
    const { error } = await supabase
      .from("learning_projects")
      .delete()
      .eq("id", courseId)
      .eq("user_id", session.user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not delete course",
        description: error.message,
      });
      return;
    }
    toast({ title: "Course deleted" });
    queryClient.invalidateQueries({ queryKey: ["dashboard-workspaces"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-unassigned-courses"] });
    queryClient.invalidateQueries({ queryKey: ["user-type"] });
    queryClient.invalidateQueries({ queryKey: ["is-designer"] });
  };

  const handleRequestDemo = () => {
    navigate("/request-demo");
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/88 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" asChild className="shrink-0" title="My Page">
              <Link to="/dashboard">
                <Home className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">My Page</span>
              </Link>
            </Button>
            <h1 className="text-lg sm:text-2xl font-heading font-semibold truncate">Distilled Intuition</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={restartTutorial} className="shrink-0">
              <HelpCircle className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Tutorial</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleRequestDemo} className="shrink-0">
              <CreditCard className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Request demo</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleSignOut} className="shrink-0">
              <LogOut className="mr-1 sm:mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        {!loading && (
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Workspaces
                </CardTitle>
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.workspaces}</div>
                <p className="text-xs text-muted-foreground">Organize your courses</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Courses
                </CardTitle>
                <GraduationCap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.courses}</div>
                <p className="text-xs text-muted-foreground">Total courses created</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Learners
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.learners}</div>
                <p className="text-xs text-muted-foreground">Enrolled across your courses</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Learner Insights - always show when we have session (loading or loaded) */}
        {session && (
          <Card className="mb-8 border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="h-5 w-5" />
                Learner Insights
              </CardTitle>
              <CardDescription>
                Learners needing attention and top performers across your courses
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {insightsLoading ? (
                <p className="text-sm text-muted-foreground">Loading learner data...</p>
              ) : learnerInsights ? (
                <>
                  {/* At-risk learners */}
                  {learnerInsights.atRiskLearners.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-amber-600 mb-3">
                        <AlertTriangle className="h-4 w-4" />
                        Needs Attention
                      </h3>
                      <div className="space-y-4">
                        {learnerInsights.atRiskLearners.map((learner) => (
                          <div
                            key={`${learner.userId}-${learner.courseId}`}
                            className="border rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{learner.email}</p>
                                <p className="text-sm text-muted-foreground">
                                  {learner.courseTitle} • {Math.round(learner.passRate)}% pass rate • Avg score: {learner.avgScore}%
                                </p>
                                {learner.lastThreeFailed && (
                                  <Badge variant="destructive" className="mt-2">Last 3 assessments failed</Badge>
                                )}
                              </div>
                              <Button variant="outline" size="sm" asChild>
                                <Link to={`/instructor/${learner.courseId}`}>View course</Link>
                              </Button>
                            </div>
                            {learner.actionPlan && (
                              <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800">
                                <p className="text-sm font-medium flex items-center gap-1 mb-2">
                                  <ClipboardList className="h-3.5 w-3.5" />
                                  AI Remedial Action Plan
                                </p>
                                <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                                  {learner.actionPlan}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Star learners */}
                  {learnerInsights.starLearners.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-green-600 mb-3">
                        <Award className="h-4 w-4" />
                        Top Performers
                      </h3>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {learnerInsights.starLearners.map((learner) => (
                          <div
                            key={`${learner.userId}-${learner.courseId}`}
                            className="border rounded-lg p-3 bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                          >
                            <p className="font-medium text-sm">{learner.email}</p>
                            <p className="text-xs text-muted-foreground">
                              {learner.courseTitle} • {Math.round(learner.passRate)}% pass rate • Avg: {learner.avgScore}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent activity */}
                  {learnerInsights.recentActivity.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Recent Activity</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {learnerInsights.recentActivity.map((act, i) => (
                          <div key={i} className="flex justify-between items-center text-sm py-2 border-b last:border-0">
                            <div>
                              <span className="font-medium">{act.learnerEmail}</span>
                              <span className="text-muted-foreground"> • {act.nodeTitle}</span>
                              <span className="text-muted-foreground text-xs"> ({act.courseTitle})</span>
                            </div>
                            <Badge variant={act.passed ? "default" : "destructive"}>
                              {act.passed ? "Passed" : "Failed"} {act.score}%
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {learnerInsights.atRiskLearners.length === 0 &&
                    learnerInsights.starLearners.length === 0 &&
                    learnerInsights.recentActivity.length === 0 && (
                      <p className="text-sm text-muted-foreground">No learner activity yet. Enroll learners to see insights.</p>
                    )}
                </>
              ) : null}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold">My Workspaces</h2>
            <p className="text-muted-foreground mt-1">
              Organize your courses and materials in workspaces
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/create")}
              data-tutorial="create-course"
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create new course
            </Button>
            <Button variant="outline" onClick={() => setShowCreateWorkspace(true)} data-tutorial="create-workspace" className="gap-2">
              <Plus className="h-4 w-4" />
              New Workspace
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading workspaces...</p>
          </div>
        ) : workspaces.length === 0 ? (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-heading">No workspaces yet</CardTitle>
              <CardDescription>
                Create your first workspace to organize courses and shared materials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setShowCreateWorkspace(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((workspace) => (
              <Card
                key={workspace.id}
                className={cn(
                  "cursor-pointer hover:shadow-lg transition-shadow",
                  dragCourseId && "ring-2 ring-primary/15",
                  dragOverWorkspaceId === workspace.id &&
                    "ring-2 ring-primary ring-offset-2 ring-offset-background scale-[1.01]"
                )}
                onDragOver={(e) => {
                  if (!dragCourseId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverWorkspaceId(workspace.id);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverWorkspaceId((id) => (id === workspace.id ? null : id));
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const courseId = e.dataTransfer.getData("text/plain");
                  if (!courseId) return;
                  lastDropAtRef.current = Date.now();
                  void handleAssignCourseToWorkspace(courseId, workspace.id);
                }}
                onClick={() => {
                  if (Date.now() - lastDropAtRef.current < 500) return;
                  navigate(`/workspace/${workspace.id}`);
                }}
              >
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{workspace.icon || "📁"}</span>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="truncate">{workspace.name}</CardTitle>
                      {workspace.description && (
                        <CardDescription className="line-clamp-2 mt-1">
                          {workspace.description}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {dragCourseId ? (
                    <p className="text-xs font-medium text-primary mb-2">Release to add this course to the workspace</p>
                  ) : null}
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      <span>{workspace.course_count} courses</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      <span>{workspace.material_count} materials</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {/* Unassigned courses */}
        {unassignedCourses.length > 0 && (
          <div className="mt-10">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Courses not in a workspace</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Drag a course onto a workspace below to add it there, or open it to edit. Courses here are only visible to you until you move or delete them.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {unassignedCourses.map((course) => (
                <Card
                  key={course.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", course.id);
                    e.dataTransfer.effectAllowed = "move";
                    setDragCourseId(course.id);
                  }}
                  onDragEnd={() => {
                    setDragCourseId(null);
                    setDragOverWorkspaceId(null);
                  }}
                  className={cn(
                    "cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow border-dashed border-2",
                    dragCourseId === course.id && "opacity-80 ring-2 ring-primary/40"
                  )}
                  onClick={() => navigate(`/project/${course.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-5 w-5 mt-1 shrink-0 text-muted-foreground" aria-hidden />
                      <div className="flex-1 min-w-0">
                        <CardTitle className="truncate text-base">{course.title}</CardTitle>
                        {course.description && (
                          <CardDescription className="line-clamp-2 mt-1">{course.description}</CardDescription>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Drag into a workspace</p>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this course?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently deletes &quot;{course.title}&quot; and its content. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => handleDeleteUnassignedCourse(course.id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Create Workspace Dialog */}
      <Dialog open={showCreateWorkspace} onOpenChange={setShowCreateWorkspace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Workspaces help you organize related courses and share materials between them
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                placeholder="e.g., AWS Certification Training"
                value={newWorkspace.name}
                onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspace-description">Description (optional)</Label>
              <Textarea
                id="workspace-description"
                placeholder="Describe what courses this workspace will contain..."
                value={newWorkspace.description}
                onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                rows={3}
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWorkspace(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkspace} disabled={creating}>
              {creating ? "Creating..." : "Create Workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
